/*
  Flickity carousel adapter - a Swiper-shaped façade over the Flickity build
  already bundled in vendor.js.

  Why: the theme shipped both libraries. Flickity (2.3.0 + AsNavFor/Fade/
  ImagesLoaded) is in vendor.js and loads on every page anyway; Swiper was a
  second ~150 KB raw / 42 KB gz bundle plus an 18 KB stylesheet, loaded on the
  product, collection and home templates purely for eight sections' carousels.
  Every Swiper feature those sections used has a Flickity equivalent - the only
  `effect` in the theme is the default 'slide', and no section uses fade or
  coverflow - so the bundle is removable once the call sites move across.

  Design: this deliberately accepts the *same root selector and option object*
  the `new Swiper(...)` calls already passed, and it tags the existing
  .swiper-wrapper / .swiper-slide markup at runtime instead of asking sections
  to rename anything. That keeps each section's diff to one call and leaves all
  of their hand-written .swiper-pagination / .swiper-button-* CSS untouched.
  assets/flickity-carousel.css supplies the base geometry those rules used to
  inherit from swiper-bundle.min.css.

  Not implemented, because nothing in this theme uses them: vertical direction,
  effects other than 'slide', freeMode, and Swiper's `watchOverflow`
  auto-disable. Anything passing those should be checked by hand rather than
  assumed to work.

  Loaded <script defer> after flickity-alias.js in layout/theme.liquid, so
  window.Flickity exists before any section's DOMContentLoaded handler runs.
*/
(function () {
  'use strict';

  var CELL_CLASS = 'fc-cell';
  var TRACK_CLASS = 'fc-track';

  function resolve(target) {
    if (!target) return null;
    return typeof target === 'string' ? document.querySelector(target) : target;
  }

  /*
    Swiper breakpoints are min-width and the top-level options act as the
    implicit smallest breakpoint, so the winning entry is the highest key that
    the viewport still satisfies.
  */
  function activeOptions(opts) {
    var resolved = {
      slidesPerView: opts.slidesPerView,
      spaceBetween: opts.spaceBetween,
      slidesPerGroup: opts.slidesPerGroup
    };
    if (!opts.breakpoints) return resolved;

    Object.keys(opts.breakpoints)
      .map(Number)
      .sort(function (a, b) {
        return a - b;
      })
      .forEach(function (min) {
        if (window.innerWidth < min) return;
        var bp = opts.breakpoints[min];
        if (bp.slidesPerView !== undefined) resolved.slidesPerView = bp.slidesPerView;
        if (bp.spaceBetween !== undefined) resolved.spaceBetween = bp.spaceBetween;
        if (bp.slidesPerGroup !== undefined) resolved.slidesPerGroup = bp.slidesPerGroup;
      });

    return resolved;
  }

  function applySizing(track, resolved) {
    track.style.setProperty('--fc-per-view', resolved.slidesPerView || 1);
    track.style.setProperty('--fc-gap', (resolved.spaceBetween || 0) + 'px');
  }

  /*
    How many positions the carousel can actually stop at - Swiper's snap count,
    which is what its pagination counted.

    Flickity keeps one entry in `slides` per cell (we never group unless the
    section asked for it), so slides.length is the *cell* count. With
    `contain: true` its _containSlides() clamps every trailing slide's target to
    the same maximum, because scrolling past that would show empty space. Those
    clamped slides are one stop, not several: 8 cells at 4-per-view is 5 stops,
    which is exactly the "1 / 5" Swiper used to display (it showed 8 before this
    was accounted for). Counting distinct targets recovers that number without
    having to re-derive Swiper's snapGrid arithmetic.
  */
  function snapCount(flkty) {
    var targets = [];
    flkty.slides.forEach(function (slide) {
      var target = Math.round(slide.target);
      if (targets.indexOf(target) === -1) targets.push(target);
    });
    return targets.length || 1;
  }

  // selectedIndex can point at one of the clamped trailing slides, which all
  // render as the last stop, so pin it to the last dot rather than overflowing.
  function displayIndex(flkty, count) {
    return Math.min(flkty.selectedIndex, count - 1);
  }

  /*
    Rebuilt rather than diffed on every `select`: these carousels have a handful
    of slides, and reusing Swiper's exact class names means each section's
    existing bullet CSS keeps applying with no changes.
  */
  function buildBullets(el, flkty) {
    el.classList.add('swiper-pagination-bullets', 'swiper-pagination-horizontal');
    el.textContent = '';

    var count = snapCount(flkty);
    var active = displayIndex(flkty, count);

    for (var i = 0; i < count; i++) {
      var bullet = document.createElement('span');
      bullet.className = 'swiper-pagination-bullet';
      if (i === active) bullet.classList.add('swiper-pagination-bullet-active');
      bullet.setAttribute('role', 'button');
      bullet.setAttribute('tabindex', '0');
      bullet.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      /* eslint-disable no-loop-func */
      (function (index) {
        bullet.addEventListener('click', function () {
          flkty.select(index);
        });
      })(i);
      /* eslint-enable no-loop-func */
      el.appendChild(bullet);
    }
  }

  function syncBullets(el, flkty) {
    var bullets = el.children;
    var active = displayIndex(flkty, bullets.length);
    for (var i = 0; i < bullets.length; i++) {
      bullets[i].classList.toggle('swiper-pagination-bullet-active', i === active);
    }
  }

  function buildFraction(el, flkty, pending) {
    el.classList.add('swiper-pagination-fraction');
    el.innerHTML =
      '<span class="swiper-pagination-current"></span> / <span class="swiper-pagination-total"></span>';
    syncFraction(el, flkty, pending);
  }

  /*
    `pending` is the number of slides a section still has to append (see
    appendCells below) - each one adds a stop, so the total shown is the final
    one from the start instead of growing as pages load.
  */
  function syncFraction(el, flkty, pending) {
    var current = el.querySelector('.swiper-pagination-current');
    var total = el.querySelector('.swiper-pagination-total');
    var count = snapCount(flkty);
    if (current) current.textContent = displayIndex(flkty, count) + 1;
    if (total) total.textContent = count + pending();
  }

  /*
    Swiper toggled .swiper-button-disabled at the ends; the sections' CSS styles
    that class, so keep feeding it. With wrapAround there are no ends.
  */
  function syncNav(prev, next, flkty, wrapAround, pending) {
    if (wrapAround) return;
    if (prev) prev.classList.toggle('swiper-button-disabled', flkty.selectedIndex === 0);
    if (next) {
      // snapCount, not slides.length: with `contain` the trailing cells share
      // one stop, so the last reachable position is snapCount - 1. Slides still
      // to be appended keep it enabled.
      next.classList.toggle(
        'swiper-button-disabled',
        flkty.selectedIndex >= snapCount(flkty) - 1 && pending() === 0
      );
    }
  }

  function equaliseCellHeights(flkty) {
    var cells = flkty.getCellElements();
    if (!cells.length) return;

    cells.forEach(function (cell) {
      cell.style.removeProperty('height');
    });

    var tallest = 0;
    cells.forEach(function (cell) {
      tallest = Math.max(tallest, cell.offsetHeight);
    });
    if (!tallest) return;

    cells.forEach(function (cell) {
      cell.style.setProperty('height', tallest + 'px', 'important');
    });

    // Cell heights changed, so Flickity's gallery height is stale.
    flkty.resize();
  }

  function themeCarousel(target, options) {
    var opts = options || {};
    var root = resolve(target);
    if (!root || typeof window.Flickity === 'undefined') return null;

    var cellSelector = opts.cellSelector || '.swiper-slide';
    var track = root.querySelector('.swiper-wrapper') || root;
    if (track.classList.contains('flickity-enabled')) return track.themeCarousel || null;

    var cells = Array.prototype.filter.call(track.children, function (child) {
      return child.matches(cellSelector);
    });
    if (!cells.length) return null;

    track.classList.add(TRACK_CLASS);
    cells.forEach(function (cell) {
      cell.classList.add(CELL_CLASS);
    });

    var resolved = activeOptions(opts);
    applySizing(track, resolved);

    var wrapAround = !!opts.loop;
    // Not a Swiper option: sections that append slides later (appendCells)
    // pass a function returning how many are still to come.
    var pending = typeof opts.pendingCells === 'function' ? opts.pendingCells : function () {
      return 0;
    };
    var flkty = new window.Flickity(track, {
      cellSelector: '.' + CELL_CLASS,
      // Swiper has no option for this (it reads dir="rtl"); sections on RTL
      // pages pass `rightToLeft: true` so the first slide starts on the right.
      rightToLeft: !!opts.rightToLeft,
      cellAlign: opts.rightToLeft ? 'right' : 'left',
      // Swiper never over-scrolled past the last slide; Flickity's `contain`
      // is that behaviour. It is ignored when wrapAround is on.
      contain: true,
      wrapAround: wrapAround,
      groupCells: resolved.slidesPerGroup > 1 ? resolved.slidesPerGroup : false,
      pageDots: false, // sections render their own .swiper-pagination element
      prevNextButtons: false, // sections render their own .swiper-button-* divs
      draggable: opts.allowTouchMove === false ? false : true,
      autoPlay: opts.autoplay ? opts.autoplay.delay || 3000 : false,
      // Swiper's pauseOnMouseEnter defaults to false; Flickity's equivalent
      // defaults to true. Follow whatever the section actually asked for.
      pauseAutoPlayOnHover: !!(opts.autoplay && opts.autoplay.pauseOnMouseEnter),
      initialIndex: opts.initialSlide || 0,
      // Cells are sized in CSS so widths never depend on images, but heights
      // do - re-lay out once the lazy-loaded images have arrived.
      imagesLoaded: true,
      adaptiveHeight: !!opts.adaptiveHeight,
      accessibility: true
    });

    var prevEl = opts.navigation ? resolve(opts.navigation.prevEl) : null;
    var nextEl = opts.navigation ? resolve(opts.navigation.nextEl) : null;

    // Named so destroy() can detach them - otherwise a rebuilt carousel would
    // still have the destroyed instance's handlers on the same buttons.
    function onPrevClick() {
      flkty.previous(wrapAround);
    }
    function onNextClick() {
      flkty.next(wrapAround);
    }
    if (prevEl) prevEl.addEventListener('click', onPrevClick);
    if (nextEl) nextEl.addEventListener('click', onNextClick);

    var pagEl = opts.pagination ? resolve(opts.pagination.el) : null;
    var isFraction = opts.pagination && opts.pagination.type === 'fraction';

    if (pagEl) {
      if (isFraction) {
        buildFraction(pagEl, flkty, pending);
      } else {
        buildBullets(pagEl, flkty);
      }
    }

    /*
      Flickity binds stopPlayer to both `uiChange` and `pointerDown`, so any
      interaction kills autoplay for the life of the page. Swiper's
      disableOnInteraction:false - which both banner sections set - keeps it
      running, so re-arm the player once the interaction has finished. These
      handlers are registered after Flickity's own, so playPlayer wins.
    */
    if (opts.autoplay && opts.autoplay.disableOnInteraction === false) {
      ['uiChange', 'dragEnd', 'staticClick'].forEach(function (evt) {
        flkty.on(evt, function () {
          flkty.playPlayer();
        });
      });
    }

    function syncControls() {
      if (pagEl) {
        if (isFraction) syncFraction(pagEl, flkty, pending);
        else syncBullets(pagEl, flkty);
      }
      syncNav(prevEl, nextEl, flkty, wrapAround, pending);
    }

    syncNav(prevEl, nextEl, flkty, wrapAround, pending);
    flkty.on('select', syncControls);

    if (opts.equalHeight !== false) equaliseCellHeights(flkty);

    /*
      Cells are measured before their lazy-loaded images have arrived, so an
      image landing changes a card's natural height and the equalised value goes
      stale. Re-equalise once per image, capture phase because `load` does not
      bubble.
    */
    if (opts.equalHeight !== false) {
      track.addEventListener(
        'load',
        function (e) {
          if (e.target && e.target.tagName === 'IMG') equaliseCellHeights(flkty);
        },
        true
      );
    }

    /*
      One debounced resize handler for everything that depends on viewport width:
      Swiper re-read its breakpoints on resize, and equalised heights have to be
      recomputed because reflowed text changes which card is tallest. Bullets are
      only rebuilt when the snap count actually changed - group size can shift
      across a breakpoint.
    */
    var resizeTimer = null;
    window.addEventListener('resize', onResize);
    function onResize() {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        var before = snapCount(flkty);
        if (opts.breakpoints) applySizing(track, activeOptions(opts));
        if (opts.equalHeight !== false) equaliseCellHeights(flkty);
        else flkty.resize();
        if (pagEl && !isFraction && snapCount(flkty) !== before) {
          buildBullets(pagEl, flkty);
        } else if (pagEl && isFraction) {
          syncFraction(pagEl, flkty, pending);
        }
      }, 150);
    }

    /*
      Adds slides to the end of a built carousel (Swiper's appendSlide) - for
      sections that load their slides in pages. Re-equalises heights and
      re-syncs the pagination/nav for the new slide count.
    */
    flkty.appendCells = function (elems) {
      if (elems && elems.length) {
        elems.forEach(function (cell) {
          cell.classList.add(CELL_CLASS);
          cells.push(cell);
        });
        flkty.append(elems);
        if (opts.equalHeight !== false) equaliseCellHeights(flkty);
      }
      // Synced even with nothing appended: the section may have just learned
      // there is nothing more to come (pendingCells dropped to 0).
      syncControls();
    };

    /*
      Swiper's `update()` recalculated after the DOM changed - the TLPC routine/
      sets sliders call it when their tab panel becomes visible, since a slider
      built inside a `display:none` panel measures as zero width. Flickity spells
      that `resize()`.
    */
    if (typeof flkty.update !== 'function') {
      flkty.update = function () {
        flkty.resize();
      };
    }

    /*
      custom-pdp.liquid destroys and rebuilds across the 768px breakpoint.
      Flickity's own destroy() undoes Flickity's DOM changes but knows nothing
      about the classes and custom properties this adapter added, and leaving
      .fc-track behind would keep applying the pre-init flex layout against the
      desktop grid. Wrap it so a destroyed carousel leaves the markup exactly as
      it was found. Extra arguments (sections pass Swiper's `true, true`) are
      ignored, as they were by Swiper's own signature here. The adapter's own
      listeners and pagination markup go too, or the dead instance keeps
      writing its stale index into the pagination on every resize.
    */
    var flickityDestroy = flkty.destroy.bind(flkty);
    flkty.destroy = function () {
      flickityDestroy();
      window.removeEventListener('resize', onResize);
      if (resizeTimer) window.clearTimeout(resizeTimer);
      if (prevEl) prevEl.removeEventListener('click', onPrevClick);
      if (nextEl) nextEl.removeEventListener('click', onNextClick);
      if (pagEl) pagEl.innerHTML = '';
      track.classList.remove(TRACK_CLASS);
      track.style.removeProperty('--fc-per-view');
      track.style.removeProperty('--fc-gap');
      cells.forEach(function (cell) {
        cell.classList.remove(CELL_CLASS);
        // Equalised heights are inline !important - they would otherwise
        // survive into the desktop grid layout and freeze its row heights.
        cell.style.removeProperty('height');
      });
      delete track.themeCarousel;
      delete root.themeCarousel;
      return flkty;
    };

    // Let sections reach the instance (e.g. the tab section drives it directly).
    track.themeCarousel = flkty;
    root.themeCarousel = flkty;

    return flkty;
  }

  window.themeCarousel = themeCarousel;
})();
